/**
 * featureUsage.ts
 *
 * Sends queued beta-feature usage counts: at startup, every five minutes and
 * whenever the tab is hidden. A tab closed mid-request keeps its queue in
 * localStorage for the next start.
 */
import { useAuthStore } from '@/stores/authStore'
import { useFeatureUsageStore } from '@/stores/featureUsageStore'

/** How often the queue is sent while the app is open, in milliseconds. */
export const FEATURE_USAGE_FLUSH_INTERVAL_MS = 5 * 60 * 1000

/**
 * Starts sending usage counts. Call once at startup, after Pinia and the
 * clients are installed.
 *
 * @returns A function that stops the timer and the listener (used by tests).
 */
export function startFeatureUsage(): () => void {
  const authStore = useAuthStore()
  const usage = useFeatureUsageStore()

  const flushIfSignedIn = () => {
    if (authStore.username) void usage.flush()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flushIfSignedIn()
  }

  flushIfSignedIn()
  const timer = setInterval(flushIfSignedIn, FEATURE_USAGE_FLUSH_INTERVAL_MS)
  document.addEventListener('visibilitychange', onVisibility)

  return () => {
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
